/**
 * `EscalationDetail` — right-hand detail panel for the HITL escalation
 * review queue (ADR-0094 Slice 3).
 *
 * Mirrors the `MandateDetail` panel chrome: sticky `top-24`,
 * `max-h-[calc(100vh-7rem)]`, `overflow-y-auto`, a `Card` wrapper, a
 * mono kicker + serif headline + a status pill, and a `dl.space-y-3`
 * grid built from the same `Row` typography.
 *
 * The panel surfaces the held order summary + the rule that fired (in
 * amber), reveals buyer detail via the reused `BuyerDetailPanel` visual
 * language (access-logged decrypt against the escalation buyer endpoint),
 * and — for owner/admin on a PENDING row — renders Approve (lime) /
 * Reject (coral) buttons gated behind a tone-aware `ConfirmDialog`.
 *
 * Resolution is handled by the parent (`Resolution`) so the optimistic
 * row removal + the 409/503 reconciliation live next to the list state;
 * this panel just calls back with the chosen decision.
 */

import { Check, ShieldX, X } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';

import type { AuditRecord, AuditRecordId, UserRole } from '@sill/types';

import { fetchEscalationBuyerDetail } from '../../api/escalation-buyer-detail.js';
import type {
  EscalationItem,
  EscalationQuote,
} from '../../api/escalations.js';
import { Card, Collapsible, ConfirmDialog, Pill, RelativeTime, Tooltip } from '../../components/atoms/index.js';
import { useDisplayTimeZone } from '../../app/time-zone-context.js';
import { formatIsoDate } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { BuyerDetailPanel } from '../audit/BuyerDetailPanel.js';
import { OriginalOrderPanel } from '../audit/OriginalOrderPanel.js';
import {
  readSettlement,
} from '../mandates/MandateDetail.js';
import {
  fromQuote,
  fromSettlement,
  fromUcpBreakdown,
  OrderMoneyBreakdown,
  type OrderMoney,
  type OrderMoneyLine,
} from '../mandates/OrderMoneyBreakdown.js';
import {
  formatMoney,
  humanizeReasonCode,
  isRefundSummary,
  refundHeadline,
  statusPill,
  timeToTimeout,
} from './escalation-format.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface EscalationDetailProps {
  item: EscalationItem;
  /** owner/admin can resolve; reviewer can view + reveal; viewer never
   *  reaches this panel (the parent gates the whole screen). */
  role: UserRole | null;
  /** True while a resolve request for THIS escalation is in flight. */
  busy: boolean;
  /** Parent-driven resolve — the optimistic update + 409/503 handling
   *  lives in `Resolution` alongside the list state. */
  onResolve: (decision: 'approve' | 'reject') => void;
  /** Active site's human domain (e.g. `shop.example.com`). The server
   *  returns only the raw `site_id` ULID; the parent threads the domain
   *  down for display. Falls back to `item.site_id` when unavailable. */
  siteDomain?: string;
  /**
   * The linked audit record for a SETTLED resolution (fetched by the
   * parent via `item.audit_record_id`). When present the panel renders
   * the same line-item names + settled money breakdown (subtotal / tax /
   * shipping / total) the Mandates detail shows. Absent on pending rows
   * (no settlement exists yet) and while the fetch is in flight — the
   * held-summary view renders unchanged.
   */
  auditRecord?: AuditRecord;
  /**
   * Pre-flight quote for a PENDING escalation (ADR-0094 follow-up) —
   * the projected all-in settlement (tax + shipping computed by the
   * rail's calculate, zero mutations) vs the mandate ceiling. Absent
   * while loading / unavailable (non-Shopify rail, unresolvable cart) —
   * the held-summary view renders unchanged.
   */
  quote?: EscalationQuote;
  /**
   * Container scroll/positioning mode (mirrors `MandateDetail`).
   * Default `'sticky'` keeps the Resolution-view chrome unchanged;
   * the dry-run `ShadowEscalationView` passes `'flat'` so the panel
   * flows inside the drawer's single `overflow-y-auto` body (no
   * nested scrollbars). `feat/dry-run-ux-polish` (2).
   */
  containerLayout?: 'sticky' | 'flat';
}

export function EscalationDetail({
  item,
  role,
  busy,
  onResolve,
  siteDomain,
  auditRecord,
  quote,
  containerLayout = 'sticky',
}: EscalationDetailProps): JSX.Element {
  const C = useC();
  const timeZone = useDisplayTimeZone();
  const [confirm, setConfirm] = useState<'approve' | 'reject' | null>(null);

  // Settled evidence (linked audit record) — drives the product-name
  // resolution on the line items and the settled money breakdown below.
  const settlement = auditRecord !== undefined ? readSettlement(auditRecord) : undefined;
  const settledIntent = auditRecord?.intent_summary;

  const presented = statusPill(item.status);
  const can_resolve = (role === 'owner' || role === 'admin') && item.status === 'pending';
  const summary = item.summary;
  // Refund escalation (ADR-0099): the held intent is a CLOSED shape — the
  // reviewable substance is the server-resolved ORIGINAL order below.
  const refund = isRefundSummary(summary);
  const countdown = timeToTimeout(item.timeout_at);
  // Timeout deadline runs red inside the final hour; muted once resolved.
  const timeoutColor =
    item.status === 'pending'
      ? countdown.imminent
        ? C.coralFg
        : C.text
      : C.muted;

  // `feat/dry-run-ux-polish` (2) — when embedded in the dry-run
  // drawer (the only `containerLayout='flat'` caller today), the
  // outer drawer body owns the single scroll. Strip the sticky
  // positioning + max-height + inner overflow region so the panel
  // flows naturally inside it.
  const cardClassName =
    containerLayout === 'flat' ? 'p-5' : 'p-5 sticky top-24 overflow-y-auto';
  const cardStyle =
    containerLayout === 'flat' ? undefined : { maxHeight: 'calc(100vh - 7rem)' };

  // Which money shape drives the Order section:
  //   1) The UCP checkout breakdown when the server attached it.
  //   2) The Shopify pre-flight quote when the rail could compute one.
  //   3) The held summary — item names resolved from `product_titles`,
  //      prices unknown ("—"). Still renders so a reviewer sees the cart.
  // Refunds never render the Order section — they use the Original-order
  // panel below (ADR-0099).
  const orderMoney: OrderMoney | null = refund
    ? null
    : item.breakdown !== undefined
      ? fromUcpBreakdown(item.breakdown)
      : item.status === 'pending' && quote !== undefined
        ? fromQuote(quote)
        : settlement !== undefined
          ? attachCeiling(fromSettlement(settlement), summary)
          : heldFallbackMoney(summary);
  // Whether the detail panel renders the Order section itself. The Order
  // section replaces the historical SettlementPanel entirely on
  // resolution — the money rows are the same (subtotal/discount/tax/
  // shipping/total vs ceiling) and we never want to show them twice.
  const renderOrderSection = orderMoney !== null;

  const quoted_total_differs =
    summary.quoted_total !== undefined
    && orderMoney !== null
    && orderMoney.total_minor !== undefined
    && Math.abs(summary.quoted_total * 100 - orderMoney.total_minor) >= 1;

  const DecisionButtons = can_resolve ? (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => setConfirm('approve')}
        disabled={busy}
        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5"
        style={{
          background: C.lime,
          color: C.ink,
          border: 'none',
          borderRadius: 2,
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 2,
          fontWeight: 600,
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}
      >
        <Check size={11} /> {busy ? 'RESOLVING…' : 'APPROVE'}
      </button>
      <button
        type="button"
        onClick={() => setConfirm('reject')}
        disabled={busy}
        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5"
        style={{
          background: C.coral,
          color: '#FFFFFF',
          border: 'none',
          borderRadius: 2,
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 2,
          fontWeight: 600,
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}
      >
        <X size={11} /> {busy ? 'RESOLVING…' : 'REJECT'}
      </button>
    </div>
  ) : item.status === 'pending' ? (
    <ReadOnlyNotice C={C} />
  ) : null;

  return (
    <Card className={cardClassName} {...(cardStyle !== undefined ? { style: cardStyle } : {})}>
      {/* Header — kicker + serif headline + status pill + countdown. */}
      <div className="flex items-start justify-between gap-2 mb-4 flex-wrap">
        <div className="min-w-0">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 4,
            }}
          >
            HELD ORDER · {summary.action.toUpperCase().replace(/_/g, ' ')}
            {summary.protocol !== undefined ? ` · VIA ${summary.protocol.toUpperCase()}` : ''}
          </div>
          <div
            style={{
              fontFamily: serifFont,
              fontSize: 22,
              color: C.text,
              letterSpacing: -0.5,
              lineHeight: 1.15,
              wordBreak: 'break-word',
            }}
          >
            {refund
              ? refundHeadline(summary)
              : productSummaryLine(item, summary) ?? 'Held order'}
          </div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: timeoutColor,
              marginTop: 6,
            }}
          >
            {item.status === 'pending' ? countdown.label : 'resolved'}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {refund ? <Pill tone="amber">REFUND</Pill> : null}
          <Pill tone={presented.tone}>{presented.label}</Pill>
          {/* ops2026 UCP checkout test (2026-10-21). Neutral pill when
              the held order is a staff-run test; the server sets the
              flag off `ucp_checkout_session.escalation_id`. */}
          {item.staff_test === true ? (
            <Tooltip content="Run from Sill's ops console to check this site's checkout. Not counted in your numbers.">
              <Pill tone="neutral">SILL TEST</Pill>
            </Tooltip>
          ) : null}
        </div>
      </div>

      {/* Decide first. On a 390px viewport the pair stays above the fold. */}
      {DecisionButtons !== null ? <div className="mb-5">{DecisionButtons}</div> : null}

      {/* Why held — the rule that fired, in a plain sentence. */}
      <section className="mb-5">
        <Kicker C={C} label="Why held" />
        <div
          className="p-3 flex items-start gap-3"
          style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2 }}
        >
          <div className="flex-1 min-w-0">
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
                fontWeight: 600,
                marginBottom: 4,
                wordBreak: 'break-word',
              }}
            >
              {summary.failed_rule_id ?? item.rule_id}
            </div>
            {whyHeldSentence(item, summary) !== null ? (
              <p
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.muted,
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                {whyHeldSentence(item, summary)}
              </p>
            ) : null}
          </div>
          <Pill tone="amber">ESCALATE</Pill>
        </div>
        {refund && summary.reason_code !== undefined ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              marginTop: 8,
              lineHeight: 1.5,
            }}
          >
            Buyer reason · {humanizeReasonCode(summary.reason_code)}
            {summary.scope !== undefined
              ? ` · ${summary.scope === 'full' ? 'full order' : 'partial — selected lines'}`
              : ''}
          </div>
        ) : null}
        {(item.status === 'rejected' || item.status === 'timed_out') ? (
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.5,
              marginTop: 8,
            }}
          >
            {item.status === 'timed_out'
              ? 'Auto-rejected — no decision before the review deadline. The order was cancelled.'
              : 'Manually rejected by an operator. The transaction was denied and the order cancelled.'}
          </p>
        ) : null}
      </section>

      {/* Order — the shared breakdown panel. Projected mode on pending
          rows (the "approve this and settle $X" prompt); settled mode
          after resolution. Refunds skip this: the reviewable substance
          is the Original order below. */}
      {renderOrderSection && orderMoney !== null ? (
        <section className="mb-5">
          <OrderMoneyBreakdown
            C={C}
            mode={item.status === 'pending' ? 'projected' : 'settled'}
            money={orderMoney}
          />
          {quoted_total_differs ? (
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                marginTop: 8,
                letterSpacing: 1,
              }}
            >
              QUOTED TOTAL · {formatMoney(summary.quoted_total!, summary.currency)} (differs
              from priced total)
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Original order — REFUND escalations only (ADR-0099 follow-up).
          The refund intent is a closed shape (no order details by design);
          the server resolved the original mandate's audit row + Shopify
          order state, site-bound. THIS is what the operator reviews. */}
      {refund ? (
        <OriginalOrderPanel
          C={C}
          original={item.original_order ?? null}
          unresolvedHint="Original order could not be resolved — refuse this refund."
          {...(role !== null ? { role } : {})}
        />
      ) : null}

      {/* Ship to — buyer reveal. The decrypt is access-logged;
          SUPPRESSED for refunds (a refund mandate carries no buyer by
          design — the buyer link lives in the Original-order panel). */}
      {!refund ? (
        <section className="mb-5">
          <Kicker C={C} label="Ship to" />
          <BuyerDetailPanel
            record_id={item.escalation_id as unknown as AuditRecordId}
            {...(role !== null ? { role_override: role } : {})}
            fetch_override={fetchEscalationBuyerDetail}
            hide_purge
          />
        </section>
      ) : null}

      {/* Timing — when the order arrived and when the hold expires. */}
      <section className="mb-5">
        <Kicker C={C} label="Timing" />
        <dl className="space-y-3">
          <Row C={C} label="Created">
            <div style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>
              {formatIsoDate(item.created_at, timeZone)}
            </div>
            <RelativeTime
              iso={item.created_at}
              className="block"
              style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginTop: 2 }}
            />
          </Row>
          {/* The auto-reject deadline only matters while the order waits or
              once it lapsed. An approved / rejected order was decided before
              it, so the row is not shown (it read "deadline passed"). */}
          {item.status === 'pending' || item.status === 'timed_out' ? (
            <Row C={C} label={item.status === 'pending' ? 'Auto-rejects' : 'Timed out'}>
              <div style={{ fontFamily: monoFont, fontSize: 12, color: timeoutColor }}>
                {formatIsoDate(item.timeout_at, timeZone)}
              </div>
              <div style={{ fontFamily: monoFont, fontSize: 11, color: timeoutColor, marginTop: 2 }}>
                {item.status === 'pending' ? countdown.label : 'Rejected automatically at the deadline'}
              </div>
            </Row>
          ) : null}
          {item.resolved_at !== undefined ? (
            <Row C={C} label="Resolved">
              <div style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>
                {formatIsoDate(item.resolved_at, timeZone)}
              </div>
              <RelativeTime
                iso={item.resolved_at}
                className="block"
                style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginTop: 2 }}
              />
            </Row>
          ) : null}
        </dl>
      </section>

      {/* Reference — ids collapsed by default; the fourth-click facts. */}
      <section className="mb-5">
        <Collapsible label="Reference">
          <dl className="space-y-3">
            <Row C={C} label="Mandate ID">
              <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, wordBreak: 'break-all' }}>
                {item.mandate_id}
              </span>
            </Row>
            <Row C={C} label="Escalation ID">
              <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, wordBreak: 'break-all' }}>
                {item.escalation_id}
              </span>
            </Row>
            <Row C={C} label="Action">
              <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>
                {summary.action}
              </span>
            </Row>
            {summary.protocol !== undefined ? (
              <Row C={C} label="Protocol">
                <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>
                  {summary.protocol.toUpperCase()}
                </span>
              </Row>
            ) : null}
            {siteDomain !== undefined || item.site_id !== undefined ? (
              <Row C={C} label="Site">
                <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, wordBreak: 'break-all' }}>
                  {siteDomain ?? item.site_id}
                </span>
              </Row>
            ) : null}
            {refund && summary.original_mandate_id !== undefined ? (
              <Row C={C} label="Original mandate">
                <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, wordBreak: 'break-all' }}>
                  {summary.original_mandate_id}
                </span>
              </Row>
            ) : null}
          </dl>
        </Collapsible>
      </section>

      <ConfirmDialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm !== null) onResolve(confirm);
        }}
        icon={confirm === 'reject' ? X : Check}
        confirmTone={confirm === 'reject' ? 'coral' : 'lime'}
        title={confirm === 'reject' ? 'Reject escalation?' : 'Approve escalation?'}
        body={
          confirm === 'reject'
            ? 'This denies the transaction and cancels the order.'
            : 'This approves the transaction and settles the payment.'
        }
        confirmLabel={confirm === 'reject' ? 'Reject' : 'Approve'}
      />
    </Card>
  );
}

/**
 * Fold the signed `max_amount` cap onto a settlement-derived money
 * shape so the settled view still renders "Authorized max" as context.
 */
function attachCeiling(
  money: OrderMoney,
  summary: EscalationItem['summary'],
): OrderMoney {
  if (money.ceiling_minor !== undefined) return money;
  if (summary.max_amount === undefined) return money;
  return { ...money, ceiling_minor: Math.round(summary.max_amount * 100) };
}

/**
 * Build an `OrderMoney` shape from the held summary (SKU + quantity +
 * product_titles). Prices are unknown so each line's total renders "—";
 * total is unknown too. We still render the panel so a reviewer sees the
 * cart first and the signed max second.
 */
function heldFallbackMoney(summary: EscalationItem['summary']): OrderMoney | null {
  // ADR-0253 2026-10-27 — prefer the merchant-signed `name` on the
  // line (AP2 CartMandate / UCP checkout) when present; fall through
  // to the catalogue-sourced `product_titles` lookup.
  const makeLine = (sku: string, quantity: number, signed_name?: string): OrderMoneyLine => {
    const title = signed_name ?? summary.product_titles?.[sku];
    return title !== undefined ? { sku, quantity, title } : { sku, quantity };
  };
  const lines: OrderMoneyLine[] =
    summary.line_items !== undefined && summary.line_items.length > 0
      ? summary.line_items.map((li) => makeLine(li.sku, li.quantity, li.name))
      : summary.sku !== undefined
        ? [makeLine(summary.sku, 1)]
        : [];
  if (lines.length === 0 && summary.max_amount === undefined) return null;
  return {
    currency: summary.currency ?? 'USD',
    lines,
    ...(summary.max_amount !== undefined
      ? { ceiling_minor: Math.round(summary.max_amount * 100) }
      : {}),
  };
}

/**
 * One-line product summary used in the header + the list rows.
 * `"Felt Hat"`, `"3 items"`, or `null` when the hold is cartless.
 */
function productSummaryLine(
  item: EscalationItem,
  summary: EscalationItem['summary'],
): string | null {
  if (item.breakdown !== undefined && item.breakdown.line_items.length > 0) {
    if (item.breakdown.line_items.length === 1) {
      const only = item.breakdown.line_items[0]!;
      return only.quantity > 1 ? `${only.quantity} × ${only.title}` : only.title;
    }
    return `${item.breakdown.line_items.length} items`;
  }
  const lines = summary.line_items;
  if (lines !== undefined && lines.length > 1) {
    return `${lines.length} items`;
  }
  // ADR-0253 2026-10-27 — prefer the merchant-signed `name` on the
  // single-line case (AP2 CartMandate / UCP checkout) before the
  // catalogue lookup; both are non-PII.
  const single_line = lines !== undefined && lines.length === 1 ? lines[0] : undefined;
  const single_sku = single_line?.sku ?? summary.sku;
  if (single_sku === undefined) return null;
  return single_line?.name ?? summary.product_titles?.[single_sku] ?? single_sku;
}

/** Plain-sentence render of a reject reason when the server gave us one. */
function whyHeldSentence(
  item: EscalationItem,
  summary: EscalationItem['summary'],
): string | null {
  if (summary.action === 'request_refund' && summary.reason_code !== undefined) {
    return `A buyer-signed refund mandate flagged ${humanizeReasonCode(summary.reason_code).toLowerCase()}. Human review required.`;
  }
  if (item.status === 'pending') {
    return 'Policy routed this transaction to review before any charge.';
  }
  return null;
}

/** Mono uppercase kicker above a section. */
function Kicker({ C, label }: { C: ReturnType<typeof useC>; label: string }): JSX.Element {
  return (
    <div
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        color: C.muted,
        letterSpacing: 2,
        marginBottom: 8,
      }}
    >
      {label.toUpperCase()}
    </div>
  );
}

function ReadOnlyNotice({ C }: { C: ReturnType<typeof useC> }): JSX.Element {
  return (
    <div
      className="p-3 flex items-center gap-2"
      style={{
        background: C.surface2,
        border: `1px dashed ${C.borderSoft}`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 10,
        color: C.muted,
        letterSpacing: 2,
      }}
    >
      <ShieldX size={11} />
      RESOLUTION · READ-ONLY FOR YOUR ROLE
    </div>
  );
}

/**
 * `Row` — label/value grid row mirroring `MandateDetail.Row` typography
 * (mono uppercase 10px kicker, 1px soft bottom border, 3-col grid). Kept
 * local rather than imported because `MandateDetail` does not export it.
 */
function Row({
  C,
  label,
  children,
}: {
  C: { muted: string; borderSoft: string };
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className="grid grid-cols-3 gap-3 pb-3"
      style={{ borderBottom: `1px solid ${C.borderSoft}` }}
    >
      <dt
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 1.5,
          paddingTop: 2,
        }}
      >
        {label.toUpperCase()}
      </dt>
      <dd className="col-span-2">{children}</dd>
    </div>
  );
}
